import { ValidationErrors } from 'final-form';
import moment, { Moment } from 'moment';
import { IAutocompleteField } from 'types/shared';
import { EMAIL_REGEX, ISNUMBER, PASSWORD_REGEX } from './constants/customRegexp';
import { isObjectEqual } from './objectEquality';

/**
 * Returns whether given field has a value
 *
 * @param value - Form string value to test against
 *
 * @returns {string | null} Either null when validation is successful or translated error message
 */
export const required = (value: string): string | null => {
  if (value && typeof value === 'string') {
    return null;
  }
  return 'validators.required';
};

/**
 * Returns whether given date field has a value
 *
 * @param value - Form string value to test against
 *
 * @returns {string | null} Either null when validation is successful or translated error message
 */
export const requiredDate = (value: Moment): string | null => {
  if (value && moment.isMoment(value) && value.isValid()) {
    return null;
  }
  return 'validators.required';
};

/**
 * Returns whether given field has a value
 *
 * @param value - Form string value to test against
 *
 * @returns {string | null} Either null when validation is successful or translated error message
 */
export const requiredAutocomplete = <T = IAutocompleteField | Array<IAutocompleteField>>(
  value: T | null,
): string | null => {
  if (Array.isArray(value) && value.length > 0) {
    return null;
  } else if (!Array.isArray(value) && value) {
    return null;
  }
  return 'validators.required';
};

/**
 * Returns whether given field is a valid password
 *
 * @param value - Form string value to test against
 *
 * @returns {string | null} Either null when validation is successful or translated error message
 */
export const password = (value: string): string | null => {
  if (PASSWORD_REGEX.test(value)) {
    return null;
  }
  return 'validators.password';
};

/**
 * Returns whether given field is a valid email
 *
 * @param value - Form string value to test against
 *
 * @returns {string | null} Either null when validation is successful or translated error message
 */
export const email = (value: string): string | null => {
  if (EMAIL_REGEX.test(value.trim())) {
    return null;
  }
  return 'validators.email';
};

/**
 * Returns whether field does not match
 *
 * @returns {string | null} Either null when validation is successful or translated error message
 */
export const comparePass = (values: {
  newPassword: string;
  confirmPassword: string;
}): ValidationErrors | Promise<ValidationErrors> | undefined => {
  if (values.newPassword !== values.confirmPassword) {
    return {
      confirmPassword: 'validators.confirmPassword',
    };
  }
  return undefined;
};

/**
 * Returns whether value is not a number
 *
 * @returns {string | null} Either null when validation is successful or translated error message
 */
export const isNumber = (value: string): string | null => {
  if (ISNUMBER.test(value) || typeof value === 'undefined') {
    return null;
  }
  return 'validators.isNumber';
};

/**
 * Returns reduced validator from given validator array
 *
 * @param validators - An array of validators
 *
 * @returns {(value: string) => string | null} A reduced validator function
 */
export const composeValidators = (
  validators: ((value: string) => string | null)[],
): ((value: string) => string | null) => (value: string) => {
  return validators.reduce((error: string | null, validator) => error || validator(value), null);
};

/**
 * Returns whether a submit object has an error assigned to it in store and has different value than the one that threw an error
 *
 * @param error - An error returned from api
 *
 * @param previousValue - Values which caused error
 *
 * @returns {string | null} Wheter given value is the same as the one that threw an error
 */
export const fetchValidatorObject = (
  error: string | undefined,
  previousValue: Record<string, unknown>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => (values: Record<string, any>): {} => {
  const errors: Record<string, unknown> = {};
  if (error && previousValue && isObjectEqual(values, previousValue)) {
    const valuesParameters = Object.keys(values);
    // eslint-disable-next-line array-callback-return
    valuesParameters.map((key) => {
      errors[key] = true;
    });
  }
  return errors;
};
